import type {
  ComponentPropsWithoutRef,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  SyntheticEvent,
} from "react"

import { CloseCircleIcon } from "../../../assets/icons"
import cx from "classnames"
import { useCallback, useEffect, useId, useRef } from "react"

import type { ModalSizeType } from "./config"

import { MODAL_SIZES } from "./config"
import styles from "./index.module.css"

export type { ModalSizeType } from "./config"
export { MODAL_SIZES } from "./config"

const sizeClassMap: Record<ModalSizeType, string> = {
  xl: styles.sizeXl,
  l: styles.sizeL,
  m: styles.sizeM,
  s: styles.sizeS,
}

export interface ModalProps extends PropsWithChildren, Pick<ComponentPropsWithoutRef<"dialog">, "id"> {
  onClose: () => void
  title?: string
  footer?: ReactNode
  isOpen?: boolean
  closeOnEscape?: boolean
  closeOnOutsideClick?: boolean
  preventClose?: boolean
  showCloseButton?: boolean
  size?: ModalSizeType
  ariaLabel?: string
  /**
   * Accessible label for the close button.
   * Override this in non-English contexts — the default is English-only.
   */
  closeButtonAriaLabel?: string
}

export const Modal = ({
  title,
  onClose,
  isOpen = true,
  children,
  footer,
  closeOnEscape = true,
  closeOnOutsideClick = true,
  preventClose = false,
  showCloseButton = true,
  size = MODAL_SIZES.M,
  ariaLabel,
  closeButtonAriaLabel = "Close modal",
  ...rest
}: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const previousFocusRef = useRef<Element | null>(null)
  const titleId = useId()

  if (process.env.NODE_ENV !== "production" && !title && !ariaLabel) {
    console.warn("[Modal] Neither `title` nor `ariaLabel` was provided. The dialog will be unlabeled for screen readers.")
  }

  useEffect(() => {
    const dialogElement = dialogRef.current
    if (!dialogElement) return

    if (isOpen && !dialogElement.open) {
      previousFocusRef.current = document.activeElement
      dialogElement.showModal()
    } else if (!isOpen && dialogElement.open) {
      dialogElement.close()
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus()
        previousFocusRef.current = null
      }
    }

    return () => {
      if (dialogElement.open) {
        dialogElement.close()
      }
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus()
        previousFocusRef.current = null
      }
    }
  }, [isOpen])

  const handleCancel = useCallback(
    (event: SyntheticEvent<HTMLDialogElement>): void => {
      event.preventDefault()
      if (closeOnEscape && !preventClose) {
        onClose()
      }
    },
    [closeOnEscape, preventClose, onClose],
  )

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDialogElement>): void => {
      if (closeOnOutsideClick && !preventClose && event.target === dialogRef.current) {
        onClose()
      }
    },
    [closeOnOutsideClick, preventClose, onClose],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDialogElement>): void => {
      if (event.key === "Escape" && (!closeOnEscape || preventClose)) {
        event.preventDefault()
        return
      }
      if (closeOnOutsideClick && !preventClose && event.key === "Enter" && event.target === dialogRef.current) {
        onClose()
      }
    },
    [closeOnEscape, closeOnOutsideClick, preventClose, onClose],
  )

  const isCloseButtonVisible = showCloseButton && !preventClose
  const showHeader = !!title || isCloseButtonVisible
  const className = cx(styles.modalContainer, sizeClassMap[size])

  return (
    <dialog
      {...rest}
      className={className}
      ref={dialogRef}
      onCancel={handleCancel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-modal={true}
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? ariaLabel : undefined}
    >
      {showHeader && (
        <div className={styles.modalHeader}>
          {title && (
            <h2 className={styles.modalTitle} id={titleId}>
              {title}
            </h2>
          )}
          {isCloseButtonVisible && (
            <button className={styles.closeButton} onClick={onClose} aria-label={closeButtonAriaLabel}>
              <CloseCircleIcon aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <div className={styles.modalContentContainer}>{children}</div>
      {footer && (
        <div data-testid="modal-footer" className={styles.modalFooter}>
          {footer}
        </div>
      )}
    </dialog>
  )
}
